import { FundClassification } from '@/models/common/FundClassification';

export function fundTypeFromClassification(classification: FundClassification): string {
  switch (classification) {
    case FundClassification.Welfare:
      return 'welfare';
    case FundClassification.Community:
      return 'community';
    case FundClassification.Kollel:
      return 'kollel';
    case FundClassification.CampaignForWelfare:
      return 'campaign_welfare';
    case FundClassification.CampaignForCommunity:
      return 'campaign_community';
    default:
      return 'unknown';
  }
}

export const FUND_FORM_UNKNOWN_FIELD = 'unknown';

/** Stable step ids for open-fund analytics (PostHog property: `step`). */
export const OPEN_FUND_ANALYTICS_STEPS = {
  FUND_DEFINITION: 'fund_definition',
  SUPPORTEDS: 'supporteds',
  APPLICANT_COORDINATORS: 'applicant_coordinators',
  TERMS_SIGNATURE: 'terms_signature',
} as const;

export type OpenFundAnalyticsStepId = (typeof OPEN_FUND_ANALYTICS_STEPS)[keyof typeof OPEN_FUND_ANALYTICS_STEPS];

/** HogQL: properties.fund_type, properties.step, properties.last_field, properties.lead_id on fund_form_* events. */
export function buildOpenFundAnalyticsProps(
  fundType: string,
  step: OpenFundAnalyticsStepId,
  extras?: { last_field?: string; field?: string; lead_id?: string | null }
): Record<string, string> {
  const props: Record<string, string> = { fund_type: fundType, step };
  if (extras?.last_field) props.last_field = extras.last_field;
  if (extras?.field) props.field = extras.field;
  if (extras?.lead_id) props.lead_id = extras.lead_id;
  return props;
}

/** Infer analytics step from react-hook-form field path when step ref is unavailable. */
export function openFundStepFromFieldName(fieldName: string, isWelfare: boolean): OpenFundAnalyticsStepId {
  if (fieldName.startsWith('supporteds') || fieldName === 'supportedAwareOfFund' || fieldName === 'supportedCanViewFund') {
    return OPEN_FUND_ANALYTICS_STEPS.SUPPORTEDS;
  }
  if (fieldName.startsWith('coordinators') || fieldName.startsWith('applicantDetails') || fieldName === 'isCoordinator') {
    return OPEN_FUND_ANALYTICS_STEPS.APPLICANT_COORDINATORS;
  }
  if (
    fieldName === 'personalInterfaceInNedarimPlus' ||
    fieldName === 'crowdfundingCampaign' ||
    fieldName === 'termsAndConditionsAccepted' ||
    fieldName === 'regulationsAccepted' ||
    fieldName === 'signature'
  ) {
    return OPEN_FUND_ANALYTICS_STEPS.TERMS_SIGNATURE;
  }
  if (
    fieldName === 'fundName' ||
    fieldName === 'fundGoals' ||
    fieldName === 'otherGoalDescription' ||
    fieldName === 'reasonSummary' ||
    fieldName === 'responsibleParty' ||
    fieldName === 'responsibleEntityName'
  ) {
    return OPEN_FUND_ANALYTICS_STEPS.FUND_DEFINITION;
  }
  return isWelfare ? OPEN_FUND_ANALYTICS_STEPS.SUPPORTEDS : OPEN_FUND_ANALYTICS_STEPS.APPLICANT_COORDINATORS;
}

export interface FundFormAbandonSession {
  submittedOk: boolean;
  abandoned: boolean;
}

/** Skip abandon when the form was submitted or we already captured abandonment. */
export function shouldCaptureFundFormAbandoned(session: FundFormAbandonSession): boolean {
  return !session.submittedOk && !session.abandoned;
}

/** Whether enough time passed since mount to treat unmount as a real abandonment (not Strict Mode flicker). */
export function shouldAbandonOnUnmount(startedAtMs: number, nowMs: number, minSessionMs = 100): boolean {
  return nowMs - startedAtMs >= minSessionMs;
}

/** Count leaf validation errors in a react-hook-form errors object (field names only — never values). */
export function countErrorFields(errors: unknown): number {
  if (!errors || typeof errors !== 'object') return 0;
  let count = 0;
  for (const val of Object.values(errors as Record<string, unknown>)) {
    if (!val || typeof val !== 'object') continue;
    if ('message' in (val as object) || 'type' in (val as object)) {
      count += 1;
      continue;
    }
    if (Array.isArray(val)) {
      for (const item of val) {
        count += countErrorFields(item);
      }
    } else {
      count += countErrorFields(val);
    }
  }
  return count;
}

/** First invalid field path from react-hook-form errors. Field names only — never values. */
export function firstErrorField(errors: unknown, prefix = ''): string | undefined {
  if (!errors || typeof errors !== 'object') return undefined;
  for (const [key, val] of Object.entries(errors as Record<string, unknown>)) {
    if (!val || typeof val !== 'object') continue;
    const path = prefix ? `${prefix}.${key}` : key;
    if ('message' in (val as object) || 'type' in (val as object)) return path;
    if (Array.isArray(val)) {
      for (let i = 0; i < val.length; i++) {
        const nested = firstErrorField(val[i], `${path}.${i}`);
        if (nested) return nested;
      }
    } else {
      const nested = firstErrorField(val, path);
      if (nested) return nested;
    }
  }
  return undefined;
}
